import { createFileRoute } from "@tanstack/react-router";
import z from "zod";

import {
  DEFAULT_NOTIFICATION_CHANNEL,
  NOTIFICATION_CHANNEL_OPTIONS,
  NOTIFICATION_CHANNELS,
} from "@/constants/sms-analytics";
import { emailTrendRangePresetSchema } from "@/schemas/email-analytics";
import { smsPerformanceSortSchema, smsTrendMetricSchema } from "@/schemas/sms-analytics";
import type { EmailOverviewSort } from "@/types/email-analytics";
import type { NotificationChannel } from "@/types/sms-analytics";
import { ScreenContent, ScreenHeader, ScreenTitle, SegmentedControl } from "@/ui";
import { isOverviewSort } from "@/utils/email-analytics";
import { EmailAnalytics } from "./-components/email-analytics";
import { SmsAnalytics } from "./-components/sms/sms-analytics";

const NotificationAnalyticsPage = () => {
  const { channel = DEFAULT_NOTIFICATION_CHANNEL } = Route.useSearch();
  const navigate = Route.useNavigate();

  const handleChannelChange = (next: NotificationChannel) => {
    void navigate({ search: { channel: next } });
  };

  return (
    <>
      <ScreenHeader>
        <ScreenTitle>Notification analytics</ScreenTitle>
      </ScreenHeader>

      <ScreenContent>
        <div className="flex flex-col gap-6">
          <SegmentedControl
            className="self-start"
            label="Channel"
            onChange={handleChannelChange}
            options={NOTIFICATION_CHANNEL_OPTIONS}
            value={channel}
          />

          {channel === NOTIFICATION_CHANNELS.sms ? <SmsAnalytics /> : <EmailAnalytics />}
        </div>
      </ScreenContent>
    </>
  );
};

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createFileRoute("/_private/notification-analytics/")({
  component: NotificationAnalyticsPage,
  validateSearch: z.object({
    channel: z.nativeEnum(NOTIFICATION_CHANNELS).optional().catch(undefined),
    from: z.string().regex(DATE_PATTERN).optional().catch(undefined),
    to: z.string().regex(DATE_PATTERN).optional().catch(undefined),
    sort: z
      .union([smsPerformanceSortSchema, z.custom<EmailOverviewSort>(isOverviewSort)])
      .optional()
      .catch(undefined),
    search: z.string().optional().catch(undefined),
    page: z.coerce.number().int().min(1).optional().catch(undefined),
    trendMetric: smsTrendMetricSchema.optional().catch(undefined),
    trendRange: emailTrendRangePresetSchema.optional().catch(undefined),
  }),
});
